import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DrawerLateral from "../Shared/DrawerLateral";
import Modal from "../../../componentes/modal/modal";
import Button from "../../../componentes/button/button";
import Input from "../../../componentes/input/input";
import { Loading } from "../../../componentes/loading/loading";
import { statusInfo } from "./statusDespesaRecorrente";
import { formatarMoeda, formatarData } from "../../../utils/solicitacoes/formatters";
import DespesaRecorrenteService from "../../../services/resource/despesaRecorrenteService";

const service = new DespesaRecorrenteService();

const NAO_PERMITE_NOVO_LANCAMENTO = ["ENVIADO", "PAGO", "MARCADO_MANUALMENTE"];
const PERMITE_CANCELAR = ["PENDENTE", "ATRASADO", "MARCADO_MANUALMENTE"];

export default function DetalheOcorrenciaDespesaRecorrente({ isOpen, onClose, ocorrenciaId, onAtualizado, onEditarRecorrencia }) {
    const navigate = useNavigate();
    const [ocorrencia, setOcorrencia] = useState(null);
    const [historico, setHistorico] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [executandoAcao, setExecutandoAcao] = useState(false);
    const [modalMarcarAberto, setModalMarcarAberto] = useState(false);
    const [observacaoMarcar, setObservacaoMarcar] = useState("");
    const [numeroSolicitacaoMarcar, setNumeroSolicitacaoMarcar] = useState("");
    const [modalCancelarFuturasAberto, setModalCancelarFuturasAberto] = useState(false);

    useEffect(() => {
        if (!isOpen || !ocorrenciaId) return;
        carregar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, ocorrenciaId]);

    function carregar() {
        setCarregando(true);
        service.buscarOcorrencia(ocorrenciaId)
            .then(res => {
                setOcorrencia(res.data);
                return service.historico(res.data.despesaRecorrenteId);
            })
            .then(res => setHistorico(res.data))
            .catch(() => setOcorrencia(null))
            .finally(() => setCarregando(false));
    }

    function fechar() {
        setOcorrencia(null);
        setHistorico([]);
        onClose();
    }

    async function criarLancamento(usarUltimo) {
        setExecutandoAcao(true);
        try {
            const res = usarUltimo
                ? await service.prefillUltimoLancamento(ocorrencia.id)
                : await service.prefillRecorrencia(ocorrencia.id);
            navigate("/solicitacoes/nova", { state: { tipoProcesso: "LANCAMENTO_DESPESA", prefill: res.data } });
        } catch {
            // toast de erro já é disparado pelo interceptor da API
        } finally {
            setExecutandoAcao(false);
        }
    }

    function confirmarMarcarLancado() {
        setExecutandoAcao(true);
        service.marcarComoLancado(ocorrencia.id, observacaoMarcar, numeroSolicitacaoMarcar || null)
            .then(() => {
                toast.success(numeroSolicitacaoMarcar
                    ? "Ocorrência vinculada à solicitação informada."
                    : "Ocorrência marcada como lançada.");
                setModalMarcarAberto(false);
                setObservacaoMarcar("");
                setNumeroSolicitacaoMarcar("");
                carregar();
                onAtualizado?.();
            })
            .catch(() => {})
            .finally(() => setExecutandoAcao(false));
    }

    function reabrir() {
        setExecutandoAcao(true);
        service.reabrir(ocorrencia.id)
            .then(() => {
                toast.success("Ocorrência reaberta.");
                carregar();
                onAtualizado?.();
            })
            .catch(() => {})
            .finally(() => setExecutandoAcao(false));
    }

    function cancelarOcorrencia() {
        setExecutandoAcao(true);
        service.cancelarOcorrencia(ocorrencia.id)
            .then(() => {
                toast.success("Ocorrência excluída.");
                // Se era PENDENTE/ATRASADO a linha foi excluída de verdade (backend) — não dá mais
                // pra buscar ela de novo, então fecha o drawer em vez de recarregar.
                fechar();
                onAtualizado?.();
            })
            .catch(() => {})
            .finally(() => setExecutandoAcao(false));
    }

    function confirmarCancelarFuturas() {
        setExecutandoAcao(true);
        service.cancelarOcorrenciasFuturas(ocorrencia.despesaRecorrenteId)
            .then(() => {
                toast.success("Ocorrências futuras excluídas e recorrência desativada.");
                setModalCancelarFuturasAberto(false);
                fechar();
                onAtualizado?.();
            })
            .catch(() => {})
            .finally(() => setExecutandoAcao(false));
    }

    function reativarRecorrencia() {
        setExecutandoAcao(true);
        service.ativarDesativar(ocorrencia.despesaRecorrenteId, true)
            .then(() => {
                toast.success("Recorrência reativada.");
                carregar();
                onAtualizado?.();
            })
            .catch(() => {})
            .finally(() => setExecutandoAcao(false));
    }

    const ultimaEnviada = historico.find(h => (h.status === "ENVIADO" || h.status === "PAGO") && h.id !== ocorrencia?.id);
    const bloqueadoParaNovoLancamento = ocorrencia && NAO_PERMITE_NOVO_LANCAMENTO.includes(ocorrencia.status);
    const cfg = ocorrencia ? statusInfo(ocorrencia.status) : null;

    return (
        <DrawerLateral isOpen={isOpen} onClose={fechar} title={ocorrencia?.descricao || "Despesa Recorrente"}>
            {carregando && <Loading label="Carregando..." />}

            {!carregando && ocorrencia && (
                <div className="flex flex-col gap-4">
                    <span className={`w-fit text-xs font-medium px-2 py-1 rounded-full ${cfg.badge}`}>{cfg.emoji} {cfg.label}</span>

                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><span className="text-muted-foreground block text-xs">Competência</span>{ocorrencia.competencia}</div>
                        <div><span className="text-muted-foreground block text-xs">Lançamento</span>{formatarData(ocorrencia.dataLancamentoProgramada)}</div>
                        <div><span className="text-muted-foreground block text-xs">Vencimento</span>{formatarData(ocorrencia.dataVencimentoProgramada)}</div>
                        <div><span className="text-muted-foreground block text-xs">Valor previsto</span>{formatarMoeda(ocorrencia.valorPrevisto)}</div>
                        <div><span className="text-muted-foreground block text-xs">Fornecedor</span>{ocorrencia.fornecedorNome || "-"}</div>
                        <div><span className="text-muted-foreground block text-xs">Filial</span>{ocorrencia.filialNome || "-"}</div>
                    </div>

                    {ocorrencia.origemNotaFiscal && (
                        <div className="text-xs bg-primary/5 border border-primary/20 rounded-md p-2">
                            <span className="text-muted-foreground block mb-0.5"><i className="fa fa-file-invoice mr-1" />Onde conseguir a nota fiscal</span>
                            <span className="text-foreground">{ocorrencia.origemNotaFiscal}</span>
                        </div>
                    )}

                    {ocorrencia.status === "MARCADO_MANUALMENTE" && (
                        <div className="text-xs text-muted-foreground bg-muted/40 rounded-md p-2">
                            Marcado por {ocorrencia.concluidoPorNome || "-"} em {formatarData(ocorrencia.dataConclusao)}.
                            {ocorrencia.observacaoConclusao && <> Obs.: {ocorrencia.observacaoConclusao}</>}
                        </div>
                    )}

                    <div className="flex flex-col gap-2 pt-2 border-t border-border">
                        {(ocorrencia.status === "ENVIADO" || ocorrencia.status === "PAGO") && ocorrencia.solicitacaoId && (
                            <Button variant="blue" label={`Ver Solicitação #${ocorrencia.solicitacaoNumero || ocorrencia.solicitacaoId}`}
                                iconLeft="fa fa-arrow-up-right-from-square" onClick={() => navigate(`/solicitacoes/${ocorrencia.solicitacaoId}`)} />
                        )}

                        {!bloqueadoParaNovoLancamento && (
                            <Button variant="blue" label="Criar Lançamento de Despesa" iconLeft="fa fa-plus" loading={executandoAcao}
                                onClick={() => criarLancamento(false)} />
                        )}

                        {ultimaEnviada && !bloqueadoParaNovoLancamento && (
                            <Button variant="outline" label={`Usar último lançamento (${ultimaEnviada.competencia})`} iconLeft="fa fa-copy"
                                loading={executandoAcao} onClick={() => criarLancamento(true)} />
                        )}

                        {!bloqueadoParaNovoLancamento && (
                            <Button variant="outline" label="Marcar como lançado" iconLeft="fa fa-check" onClick={() => {
                                setObservacaoMarcar("");
                                setNumeroSolicitacaoMarcar("");
                                setModalMarcarAberto(true);
                            }} />
                        )}

                        {ocorrencia.status === "MARCADO_MANUALMENTE" && (
                            <Button variant="outline" label="Reabrir" iconLeft="fa fa-rotate-left" loading={executandoAcao} onClick={reabrir} />
                        )}

                        <Button variant="outline" label="Editar recorrência" iconLeft="fa fa-pen"
                            onClick={() => onEditarRecorrencia?.(ocorrencia.despesaRecorrenteId)} />

                        {PERMITE_CANCELAR.includes(ocorrencia.status) && (
                            <Button variant="outline" label="Excluir esta ocorrência" iconLeft="fa fa-trash"
                                loading={executandoAcao} onClick={cancelarOcorrencia} />
                        )}

                        {ocorrencia.despesaRecorrenteAtiva ? (
                            <Button variant="red" label="Excluir ocorrências futuras" iconLeft="fa fa-calendar-xmark"
                                onClick={() => setModalCancelarFuturasAberto(true)} />
                        ) : (
                            <Button variant="outline" label="Reativar recorrência" iconLeft="fa fa-rotate-right"
                                loading={executandoAcao} onClick={reativarRecorrencia} />
                        )}
                    </div>

                    {historico.length > 0 && (
                        <div className="pt-2 border-t border-border">
                            <h4 className="text-sm font-semibold text-foreground mb-2">Histórico</h4>
                            <div className="flex flex-col gap-1.5">
                                {historico.map(h => {
                                    const hcfg = statusInfo(h.status);
                                    return (
                                        <div key={h.id} className="flex items-center justify-between text-xs">
                                            <span className="text-foreground">{h.competencia}</span>
                                            <span className={`px-2 py-0.5 rounded-full ${hcfg.badge}`}>{hcfg.emoji} {hcfg.label}</span>
                                            {h.solicitacaoId ? (
                                                <button className="text-primary hover:underline" onClick={() => navigate(`/solicitacoes/${h.solicitacaoId}`)}>
                                                    #{h.solicitacaoNumero}
                                                </button>
                                            ) : <span className="text-muted-foreground">-</span>}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <Modal
                isOpen={modalMarcarAberto}
                onClose={() => setModalMarcarAberto(false)}
                title="Marcar como lançado"
                subtitle={`Você está informando que esta despesa já foi lançada. Competência: ${ocorrencia?.competencia || "-"}.`}
                footer={
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" label="Cancelar" onClick={() => setModalMarcarAberto(false)} />
                        <Button variant="blue" label="Confirmar" loading={executandoAcao} onClick={confirmarMarcarLancado} />
                    </div>
                }
            >
                <div className="p-4 flex flex-col gap-4">
                    <Input
                        label="Número da solicitação (opcional)"
                        value={numeroSolicitacaoMarcar}
                        onChange={e => setNumeroSolicitacaoMarcar(e.target.value)}
                        placeholder="Ex.: 4582"
                        helpText="Se essa despesa já foi lançada por uma solicitação existente do mesmo fornecedor, informe o número aqui para vincular em vez de apenas marcar."
                    />
                    <div>
                        <label className="text-sm font-medium text-foreground/80 block mb-1.5">Observação (opcional)</label>
                        <textarea
                            value={observacaoMarcar}
                            onChange={e => setObservacaoMarcar(e.target.value)}
                            rows={3}
                            className="w-full text-sm border border-input rounded-md p-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
                        />
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={modalCancelarFuturasAberto}
                onClose={() => setModalCancelarFuturasAberto(false)}
                title="Excluir ocorrências futuras"
                subtitle="Todas as ocorrências pendentes/atrasadas desta despesa recorrente serão excluídas (não é possível desfazer) e a recorrência será desativada — ela para de gerar meses novos. Use quando a conta em si foi cancelada."
                footer={
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" label="Voltar" onClick={() => setModalCancelarFuturasAberto(false)} />
                        <Button variant="red" label="Excluir tudo" loading={executandoAcao} onClick={confirmarCancelarFuturas} />
                    </div>
                }
            >
                <div className="p-4 text-sm text-muted-foreground">
                    As ocorrências já lançadas ou marcadas manualmente não são excluídas, continuam no histórico.
                    Você pode reativar a recorrência depois, se precisar — mas os meses excluídos não voltam sozinhos.
                </div>
            </Modal>
        </DrawerLateral>
    );
}
